import { ArrowUpTrayIcon } from '@heroicons/react/24/outline';
import { FocusEvent, memo, MouseEvent, useEffect, useState } from 'react';

import CatalogAttribute from '@/components/catalog-attributes/CatalogAttribute';
import { isWebLinkRenderer } from '@/components/catalog-attributes/webLinkRenderer';
import { Checkbox } from '@/components/core/Checkbox';
import { AttributeGridCell } from '@/components/datamodel/AttributeGridCell';
import { cn } from '@/services/utils';
import {
    DataModelAttribute,
    DataModelAttributeRenderer,
    DataModelAttributeTypeName,
    PersistedItem,
} from '@/types/DataModel';

import { HitListDimensionalRead } from './HitListDimensionalRead';
import { useCellEditableCondition } from './hooks/useCellEditableCondition';
import { useEditingRow } from './hooks/useEditingRow';
import { syncHitListCellTitle } from './syncHitListCellTitle';

/**
 * Placeholder icon shown in read-only browse cells whose asset value is
 * empty. Mirrors the legacy AngularJS grid, which rendered a small photo /
 * document glyph so the user could see the cell is an asset slot before
 * double-clicking to edit.
 */
const isEmptyAssetAttribute = (attribute: DataModelAttribute) => {
    switch (attribute.typeName || attribute.baseClass) {
        case DataModelAttributeTypeName.Image:
        case DataModelAttributeTypeName.Document:
            return true;
        default:
            return false;
    }
};

const isHitListIconAttribute = (attribute: DataModelAttribute) => {
    const type = attribute.typeName || attribute.baseClass;
    return (
        type === DataModelAttributeTypeName.Image ||
        type === DataModelAttributeTypeName.Document
    );
};

/** Read-only hierarchy link (Children Link). The icon sits in the cell center. */
const isHitListLinkAttribute = (attribute: DataModelAttribute) =>
    isWebLinkRenderer(attribute);

/** Single-option boolean, rendered as a checkbox in collection grids. */
const isHitListCheckboxAttribute = (attribute: DataModelAttribute) =>
    (attribute.typeName || attribute.baseClass) === DataModelAttributeTypeName.Boolean &&
    attribute.params?.renderer === DataModelAttributeRenderer.SingleBoolean;

/** Language-key attributes. The key menu stays available outside edit mode. */
const isHitListDimensionalAttribute = (attribute: DataModelAttribute) => {
    const type = attribute.typeName || attribute.baseClass;
    return (
        type === DataModelAttributeTypeName.Dimensional ||
        type === DataModelAttributeTypeName.MultiLineDimensional
    );
};

// The row form mounts only while a cell is editing, which remounts the
// cell. Remember the language key so the menu does not jump back.
const dimensionalKeyByCell = new Map<string, string>();

const dimensionalCellKey = (item: PersistedItem, attribute: DataModelAttribute) =>
    `${item.primaryKey__}:${attribute.name}`;

const singleBooleanOptionKey = (attribute: DataModelAttribute): string => {
    const values = attribute.params?.values;
    if (Array.isArray(values) && values.length > 0 && typeof values[0] === 'object') {
        return String(values[0].key);
    }
    return 'true';
};

interface EditableItemCellProps {
    attribute: DataModelAttribute;
    item: PersistedItem;
}

/**
 * Build the string used for the native browser tooltip on hover. We keep
 * this narrow on purpose: for primitive values (string / number /
 * boolean) the raw item value is exactly what CatalogAttribute renders
 * in most attribute types, so it matches the visible-then-truncated
 * text. For structured values (arrays, objects, references) we fall
 * back to no title rather than dumping `[object Object]` on the user;
 * those cells are typically icons or nested tables where the tooltip
 * wouldn't add anything anyway.
 */
const getCellTooltip = (
    item: PersistedItem,
    attribute: DataModelAttribute,
): string | undefined => {
    const value = item[attribute.name];
    if (value == null) return undefined;
    if (
        typeof value === 'string' ||
        typeof value === 'number' ||
        typeof value === 'boolean'
    ) {
        return String(value);
    }
    return undefined;
};

/**
 * Grid cell used by the browse items list. Reading mode shows the value
 * the way a collection cell does: the control fills the cell, and a row
 * hover draws the collection border on the cell edge. Image, document,
 * and checkbox cells stay borderless. A checkbox is always visible and
 * toggles on one click. Other fields still enter edit mode on double-click,
 * then render the same `<AttributeGridCell>` editors as a collection.
 */
const CoreEditableItemCell = ({ attribute, item }: EditableItemCellProps) => {
    const editingRow = useEditingRow();
    const { editable, hidden, categoryMismatch, editableIfInCategory } = useCellEditableCondition(
        item,
        attribute,
    );

    const isThisCellBeingEdited =
        editingRow?.editingRowId === item.primaryKey__ &&
        editingRow?.editingAttributeName === attribute.name;
    const isDimensionalCell = isHitListDimensionalAttribute(attribute);
    // A column can be empty because the value is null, or because this
    // item's category does not list the attribute (Base Demo Item only
    // has GTIN). Double-click still opens an empty editor, same as an
    // empty Simple Integer Length on an item that does own the attribute.
    // State-hidden and readonly columns stay closed.
    const canEdit = categoryMismatch ? editableIfInCategory : editable;
    const dimensionStorageKey = dimensionalCellKey(item, attribute);
    const [activeDimension, setActiveDimensionState] = useState<string | undefined>(
        () => dimensionalKeyByCell.get(dimensionStorageKey),
    );
    const setActiveDimension = (key: string) => {
        dimensionalKeyByCell.set(dimensionStorageKey, key);
        setActiveDimensionState(key);
    };

    useEffect(() => {
        if (!isThisCellBeingEdited || !isDimensionalCell) return;
        const focusValue = () => {
            const input = document.querySelector(
                '[data-editing-cell="true"] input, [data-editing-cell="true"] textarea',
            );
            if (!(input instanceof HTMLElement)) return false;
            input.focus();
            return document.activeElement === input;
        };
        if (focusValue()) return;
        const timer = window.setInterval(() => {
            if (focusValue()) window.clearInterval(timer);
        }, 50);
        const stop = window.setTimeout(() => window.clearInterval(timer), 500);
        return () => {
            window.clearInterval(timer);
            window.clearTimeout(stop);
        };
    }, [isThisCellBeingEdited, isDimensionalCell]);

    const handleDoubleClick = () => {
        // LAX-10059: double-click edits this cell only. Hierarchy tables
        // stay on single-click and are not routed through here.
        if (!canEdit) return;
        editingRow?.beginEdit(item, attribute.name);
    };

    const handleUploadClick = (event: MouseEvent) => {
        event.stopPropagation();
        if (!canEdit) return;
        editingRow?.beginEdit(item, attribute.name, { openUpload: true });
    };

    const handleFocus = () => {
        // Same as laxGridStartCellEdit for keyboard navigation / focus:
        // once the user is inside an editor, the row is actively being
        // edited so the store queue must hold off (`shouldSkip`).
        if (!editingRow) return;
        if (!canEdit) return;
        editingRow.isEditingActiveRef.current = true;
    };

    const handleBlur = (event: FocusEvent<HTMLDivElement>) => {
        // Opening the language menu moves focus into a portaled list.
        // That is still this cell; don't treat it as leaving edit mode.
        const next = event.relatedTarget;
        if (
            (next instanceof Element && next.closest(
                '.select-content, .select-item, [data-radix-portal], [role="listbox"], [role="option"]',
            ))
            || document.querySelector('.select-content, [role="listbox"]')
        ) {
            return;
        }
        // Legacy laxGridStopCellEdit: clear rowEditingActive then
        // force-flush the store queue for immediate save.
        editingRow?.endEdit();
    };

    if (hidden && !canEdit) {
        // Hidden by attribute state, or not an inline editor (readonly,
        // reference, composite). Category mismatch alone still renders
        // an empty cell so double-click can open the editor.
        return null;
    }

    if (!isThisCellBeingEdited) {
        // Reading mode stretches to the parent cell so empty cells stay
        // valid double-click targets (LAX-10014). The hover border sits on
        // the cell edge: no inset margin. Checkbox and asset cells omit it.
        // An empty Children Link still gets the border; a filled one shows
        // only the icon.
        //
        // Long values collapse to one line. Hover shows the full visible
        // text (legacy `ui-grid-browser-tooltips`).
        const hasValue = Boolean(item[attribute.name]);
        const showUploadIcon = !hasValue && isEmptyAssetAttribute(attribute);
        const tooltip = hasValue ? getCellTooltip(item, attribute) : undefined;
        const isIconCell = isHitListIconAttribute(attribute);
        const isLinkCell = isHitListLinkAttribute(attribute);
        const isCheckboxCell = isHitListCheckboxAttribute(attribute);
        // Empty Children Link is readonly, so canEdit is false, but the
        // row hover border still marks the empty cell. A filled link
        // shows the icon with no border.
        const showCellBorder =
            (isLinkCell && !hasValue) ||
            (canEdit && !isIconCell && !isLinkCell && !isCheckboxCell);
        const optionKey = isCheckboxCell ? singleBooleanOptionKey(attribute) : undefined;
        const isChecked = optionKey != null && item[attribute.name] === optionKey;

        if (isDimensionalCell) {
            return (
                <HitListDimensionalRead
                    attribute={attribute}
                    item={item}
                    editable={canEdit}
                    activeDimension={activeDimension}
                    onActiveDimensionChange={setActiveDimension}
                    onEdit={handleDoubleClick}
                />
            );
        }

        const handleCheckboxClick = (event: MouseEvent) => {
            event.stopPropagation();
            if (!canEdit || !optionKey) return;
            // The document click listener (capture) may have scheduled a
            // leave because this cell is not the one already being edited.
            // This click is the edit, so keep the row in edit mode.
            editingRow?.cancelScheduledLeave();
            editingRow?.beginEdit(item, attribute.name, {
                initialPatch: { [attribute.name]: isChecked ? null : optionKey },
            });
        };

        return (
            <div
                data-hit-list-icon-cell={isIconCell || isLinkCell ? 'true' : undefined}
                data-hit-list-checkbox-cell={isCheckboxCell ? 'true' : undefined}
                data-hit-list-editable={canEdit ? 'true' : undefined}
                onDoubleClick={isCheckboxCell ? undefined : (event) => {
                    event.stopPropagation();
                    handleDoubleClick();
                }}
                onClick={isCheckboxCell ? handleCheckboxClick : undefined}
                onMouseEnter={syncHitListCellTitle}
                title={isCheckboxCell ? undefined : tooltip}
                className={cn(
                    'tw-flex tw-h-full tw-w-full tw-min-h-0 tw-min-w-0 tw-items-center tw-text-t-sm lg:tw-text-t-base tw-text-t-primary',
                    'hit-list-cell tw-box-border tw-rounded tw-border tw-border-solid tw-border-transparent',
                    isIconCell || isLinkCell || isCheckboxCell ? 'tw-justify-center' : 'tw-justify-start tw-truncate tw-px-2',
                    showCellBorder && 'hit-list-editable-affordance',
                )}
                tabIndex={0}
            >
                {isCheckboxCell && (
                    <Checkbox
                        isChecked={isChecked}
                        disabled={!canEdit}
                        className="tw-pointer-events-none"
                        tabIndex={-1}
                    />
                )}
                {!isCheckboxCell && hasValue && (
                    <CatalogAttribute
                        attribute={attribute}
                        item={item}
                        withoutLabel={true}
                        gridMode={true}
                        singleLine={true}
                    />
                )}
                {showUploadIcon && (
                    <button
                        type="button"
                        className="tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center tw-bg-transparent tw-border-0 tw-p-0 tw-cursor-pointer"
                        onClick={handleUploadClick}
                    >
                        <ArrowUpTrayIcon className="hit-list-upload-icon tw-w-6 tw-aspect-square tw-icon-inactive" />
                    </button>
                )}
            </div>
        );
    }

    // Editing path: rely on the row-level FormProvider mounted by
    // EditableRowFormProvider. `AttributeGridCell` uses the shared
    // per-type editor switch (also used by Collection).
    //
    // The `data-editing-cell` attribute is a DOM anchor for the row-level
    // click-outside detection in `EditableRowFormProvider`: because a
    // React `<FormProvider>` has no DOM presence and we cannot inject a
    // wrapper element between `<tr>` and its `<td>` children, the provider
    // walks up from any editing cell to find the row's `<tr>` at mount.
    return (
        <div
            data-editing-cell="true"
            data-hit-list-icon-cell={isHitListIconAttribute(attribute) ? 'true' : undefined}
            data-hit-list-checkbox-cell={isHitListCheckboxAttribute(attribute) ? 'true' : undefined}
            className={cn(
                'tw-m-1 tw-box-border tw-flex tw-shrink-0 tw-items-stretch tw-overflow-visible',
                'tw-h-[calc(2.5rem-0.5rem)] tw-max-h-[calc(2.5rem-0.5rem)]',
                'tw-w-[calc(100%-0.5rem)] tw-min-w-0 tw-max-w-[calc(100%-0.5rem)]',
                'tw-text-t-sm lg:tw-text-t-base tw-text-t-primary',
                // Asset cells have no text field. Keep the tray icon in the
                // middle of the column, including after the upload dialog
                // closes and this editor is still mounted.
                (isHitListIconAttribute(attribute) || isHitListCheckboxAttribute(attribute)) && 'tw-justify-center',
            )}
            onFocusCapture={handleFocus}
            onBlurCapture={handleBlur}
            onDoubleClick={handleDoubleClick}
        >
            <AttributeGridCell
                attribute={attribute}
                fieldPath={attribute.name}
                rowData={item}
                className="tw-h-full tw-w-full"
                readonly={!canEdit}
                trayUpload={true}
                openCalendarOnMount
                openUploadOnMount={editingRow?.pendingUploadAttribute === attribute.name}
                onUploadOpened={editingRow?.clearPendingUpload}
                onUploadDismissed={editingRow?.discardEdit}
                activeDimension={isDimensionalCell ? activeDimension : undefined}
                onActiveDimensionChange={isDimensionalCell ? setActiveDimension : undefined}
            />
        </div>
    );
};

/**
 * Memoized because the parent items list re-renders on every fetch and
 * we do not want the read-only cell (which does non-trivial rendering of
 * dates / options / images) to re-run when its own data hasn't changed.
 */
const EditableItemCell = memo(
    CoreEditableItemCell,
    (prev, next) =>
        prev.item === next.item &&
        prev.attribute.name === next.attribute.name,
);

export { EditableItemCell };
